import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RevealWords } from "./Reveal";
import { FAQ } from "@/constants/testIds";

const QUESTIONS = [
	{
		q: "What exactly is Natant AI building?",
		a: "An AI-native operating system for business — a composable, agentic substrate that unifies ERP, CRM, commerce and adjacent functions into one intelligent central nervous system. Not another dashboard bolted onto legacy stacks.",
	},
	{
		q: "Who is this for?",
		a: "Global enterprises whose legacy stack was designed for a world that no longer exists — and forward operators who want to leapfrog into the agentic era rather than patch what they already have.",
	},
	{
		q: "How is this different from adding AI to existing software?",
		a: "Because everything below the AI layer was written for a passive, record-keeping world. Composable + agentic + AI-native has to be built ground-up. It is a jump as significant as the move from DOS to Windows.",
	},
	{
		q: "Why Dubai?",
		a: "The Middle East offers a launchpad free of 30-year-old infrastructure debt — allowing our first cohort to jump directly into a category-defining, AI-native stack.",
	},
	{
		q: "What is Cohort 1?",
		a: "Three founding companies who deploy the OS alongside our team — one per company size. Two seats are already taken; the mid-sized seat is open. Applications close once filled.",
	},
	{
		q: "How do I apply?",
		a: "Use the Apply for Cohort 1 flow on this site — one paragraph in your own words is all it takes. Or write directly to cohort1@natant.ai.",
	},
	{
		q: "Where can I read the full thesis?",
		a: "The working note (Whitepaper 01 — The Intelligent Enterprise) is available on natant.ai/whitepaper. Twelve sections. Fourteen-minute read.",
	},
];

function Item({ index, q, a }) {
	const [open, setOpen] = useState(false);
	const n = String(index + 1).padStart(2, "0");

	return (
		<motion.div
			data-testid={FAQ.item}
			initial={{ opacity: 0, y: 14 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.3 }}
			transition={{ duration: 0.55, delay: index * 0.03 }}
			className="border-b border-[var(--border-line)]"
		>
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				data-testid={FAQ.trigger}
				className="group w-full flex items-start justify-between gap-6 py-6 md:py-7 text-left"
			>
				<div className="flex items-baseline gap-4 md:gap-6 min-w-0">
					<span className="font-mono text-[11px] tracking-[0.22em] text-[var(--bone-dim)] shrink-0 pt-1">
						{n}
					</span>
					<span
						className={`font-display text-lg md:text-2xl transition-colors ${
							open ? "text-[var(--terracotta)]" : "text-[var(--bone)] group-hover:text-[var(--terracotta)]"
						}`}
					>
						{q}
					</span>
				</div>
				<motion.span
					animate={{ rotate: open ? 45 : 0 }}
					transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
					className={`shrink-0 w-8 h-8 border border-[var(--border-line)] flex items-center justify-center text-lg ${
						open ? "border-[var(--terracotta)] text-[var(--terracotta)]" : "text-[var(--bone-dim)]"
					}`}
					aria-hidden
				>
					+
				</motion.span>
			</button>
			<AnimatePresence initial={false}>
				{open && (
					<motion.div
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: "auto", opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
						className="overflow-hidden"
					>
						<p className="text-[var(--bone-dim)] text-base md:text-lg leading-relaxed max-w-[62ch] pl-10 md:pl-14 pb-8">
							{a}
						</p>
					</motion.div>
				)}
			</AnimatePresence>
		</motion.div>
	);
}

export default function Faq() {
	return (
		<section
			id="faq"
			data-testid={FAQ.section}
			className="relative py-12 md:py-16 border-t border-[var(--border-line)] bg-[var(--obsidian)] text-[var(--bone)]"
		>
			<div className="max-container">
				<div className="grid grid-cols-12 gap-6 mb-10">
					<div className="col-span-12 md:col-span-3">
						<div className="eyebrow mb-3">07</div>
						<h2 className="font-display text-3xl md:text-4xl font-semibold tracking-[-0.03em] mb-4">
							Questions
						</h2>
						<div className="text-sm text-[var(--bone-dim)] max-w-[22ch]">
							Ask us anything else at
							<a
								href="mailto:hello@natant.ai"
								className="text-[var(--bone)] neon-underline hover:text-[var(--terracotta)] transition-colors"
							>
								{" "}hello@natant.ai
							</a>
							.
						</div>
					</div>
					<div className="col-span-12 md:col-span-9">
						<h2 className="ctrl-h1 text-[13vw] md:text-[7vw]">
							<span className="block">
								<RevealWords trigger="inView" text="Any questions?" className="neon-accent" />
							</span>
						</h2>
					</div>
				</div>

				<div className="grid grid-cols-12 gap-6">
					<div className="col-span-12 md:col-start-4 md:col-span-9 border-t border-[var(--border-line)]">
						{QUESTIONS.map((it, i) => (
							<Item key={it.q} index={i} q={it.q} a={it.a} />
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
